import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import {
  finishInstagramConnectAttempt,
  getInstagramConnectAttempt,
} from '@/Domain/components/Integration/Instagram/instagramConnectAnalytics';
import {
  MESSENGER_AWAITING_PAGE_SELECTION,
  META_CALLBACK_ORIGIN,
} from '@/Domain/components/Integration/metaOAuth';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

export type MetaIntegrationType = 'messenger' | 'instagram';

export type MetaConnectionResult = {
  type: `${MetaIntegrationType}_connection_result`;
  integration_type: MetaIntegrationType;
  status: 'success' | 'error';
  title: string;
  message: string;
  reason_code?: string;
};

const INTEGRATION_LABELS: Record<MetaIntegrationType, string> = {
  messenger: 'Facebook Messenger',
  instagram: 'Instagram',
};

const stripHtml = (html: string): string =>
  html
    .replace(/<li[^>]*>/gi, '\n• ')
    .replace(/<\/li>/gi, '')
    .replace(/<br\s*\/?>(\s*)/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[ \t]+/g, ' ')
    // A tag closing mid-sentence becomes a space, so `</b>.` would read "Page access .".
    .replace(/[ \t]+([.,;:!?])/g, '$1')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

const isMetaConnectionResult = (data: unknown): data is MetaConnectionResult => {
  if (!data || typeof data !== 'object') return false;
  const t = (data as { type?: unknown }).type;
  return (
    typeof t === 'string' &&
    (t === 'messenger_connection_result' || t === 'instagram_connection_result')
  );
};

/**
 * Surfaces the result of a Meta OAuth popup back into the parent app.
 *
 * The OAuth popup posts a `<integration>_connection_result` message before
 * closing; without a listener on the opener side, the user only sees the
 * popup-internal feedback and the host page can't react. WhatsApp owns its
 * own listener inside the WhatsAppPhoneNumbersManager sheet because it has
 * sheet-specific state to update; this covers messenger and instagram.
 */
export const MetaCallbackListener = (): null => {
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== META_CALLBACK_ORIGIN) return;
      if (!isMetaConnectionResult(event.data)) return;
      const result = event.data;
      const label = INTEGRATION_LABELS[result.integration_type];
      const instagramAttempt =
        result.integration_type === 'instagram'
          ? getInstagramConnectAttempt(event.source)
          : undefined;

      // Messenger's OAuth half only stages Pages; the connect funnel's terminal event
      // belongs to the pick that follows (MessengerSheet), or it would count a merchant
      // who never picked as connected.
      const isAwaitingSelection = result.reason_code === MESSENGER_AWAITING_PAGE_SELECTION;

      if (instagramAttempt) {
        finishInstagramConnectAttempt(
          capture,
          instagramAttempt,
          result.status,
          result.reason_code ?? null,
          'popup_callback',
        );
      }
      if (result.status === 'success') {
        if (!instagramAttempt && !isAwaitingSelection) {
          trackIntegrationConnectSucceeded(capture, result.integration_type, {
            reason_code: result.reason_code ?? null,
          });
        }
        toast.success(result.title || `${label} connected`, {
          description: stripHtml(result.message),
          duration: 6000,
        });
      } else {
        if (!instagramAttempt) {
          trackIntegrationConnectFailed(
            capture,
            result.integration_type,
            result.reason_code ?? 'error',
          );
        }
        toast.error(result.title || `Couldn't connect ${label}`, {
          description: stripHtml(result.message),
          duration: 15000,
        });
      }

      capture(PostHogEventEnum.MetaIntegrationCallback, {
        integration_type: result.integration_type,
        status: result.status,
        reason_code: result.reason_code ?? null,
        ...(instagramAttempt ? { scenario_id: instagramAttempt.scenarioId } : {}),
      });

      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      if (result.integration_type === 'messenger') {
        queryClient.invalidateQueries({ queryKey: [QueryKey.integration.messengerPages] });
      }
      if (result.integration_type === 'instagram') {
        // The state was single-use; the next attempt needs a freshly issued one.
        queryClient.invalidateQueries({ queryKey: [QueryKey.integration.instagramOauthState] });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.integration.get, 'instagram'],
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient, capture]);

  return null;
};
